import React, { useState, useEffect } from "react";
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Card, Button, TableContainer, Paper,
  CircularProgress, Alert, MenuItem, Select, TextField,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Tooltip, Snackbar, FormControl, InputLabel, Chip,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import Swal from 'sweetalert2';
import axios from "axios";
import { useSelector } from 'react-redux';
import { apiHost } from '../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
  return `${protocol}://${baseUrl}`;
};

const DEFAULT_TAKEAWAY_OPTIONS = ['GRAB', 'GOJEK', 'SHOPEE FOOD', 'AMBIL SENDIRI', 'DELIVERY'];

export const TakeAwayData = () => {
  const user = useSelector((state) => state.auth.user);
  const [takeaways, setTakeaways] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', cabanguuid: '' });
  const [isEditing, setIsEditing] = useState(false);

  const apiBaseUrl = getApiBaseUrl();

  const fetchTakeaways = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/gettakeaway`, { withCredentials: true });
      setTakeaways(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal mengambil data takeaway');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/cabang`, { withCredentials: true });
      const data = response.data.data || [];
      const adminBranchUuid = user?.cabang?.uuid || user?.cabanguuid || null;
      if (user && user.role === 'admin') {
        let myBranch = data.find(b => b.uuid === adminBranchUuid);
        if (!myBranch && user?.cabang) myBranch = { uuid: user.cabang.uuid, namacabang: user.cabang.namacabang };
        setBranches(myBranch ? [myBranch] : []);
      } else {
        setBranches(data);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: formData.name };
      if (formData.cabanguuid) payload.cabanguuid = formData.cabanguuid;

      if (isEditing) {
        await axios.put(`${apiBaseUrl}/updatetakeaway/${currentItem.uuid}`, payload, { withCredentials: true });
        setSnackbarMessage('Takeaway berhasil diperbarui');
      } else {
        await axios.post(`${apiBaseUrl}/createtakeaway`, payload, { withCredentials: true });
        setSnackbarMessage('Takeaway berhasil ditambahkan');
      }
      setSnackbarOpen(true);
      setOpenDialog(false);
      fetchTakeaways();
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || 'Operasi gagal');
      setSnackbarOpen(true);
    }
  };

  const handleDelete = (uuid) => {
    Swal.fire({
      title: 'Yakin ingin menghapus?',
      text: 'Data tidak dapat dikembalikan!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ya, hapus!',
      cancelButtonText: 'Batal'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${apiBaseUrl}/deletetakeaway/${uuid}`, { withCredentials: true });
          setSnackbarMessage('Takeaway berhasil dihapus');
          setSnackbarOpen(true);
          fetchTakeaways();
        } catch (err) {
          setSnackbarMessage(err.response?.data?.message || 'Gagal menghapus takeaway');
          setSnackbarOpen(true);
        }
      }
    });
  };

  const handleEditClick = (item) => {
    setCurrentItem(item);
    setFormData({ name: item.name, cabanguuid: item.cabanguuid || '' });
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleNew = () => {
    setCurrentItem(null);
    const defaultCabang = user?.role === 'admin'
      ? (user?.cabang?.uuid || user?.cabanguuid || '')
      : '';
    setFormData({ name: '', cabanguuid: defaultCabang });
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleQuickAdd = (name) => {
    const defaultCabang = user?.role === 'admin'
      ? (user?.cabang?.uuid || user?.cabanguuid || '')
      : '';
    setFormData({ name, cabanguuid: defaultCabang });
    setIsEditing(false);
    setCurrentItem(null);
    setOpenDialog(true);
  };

  useEffect(() => {
    fetchTakeaways();
    fetchBranches();
  }, [user]);

  const isAdmin = user && user.role === 'admin';
  const isSuperadmin = user && user.role === 'superadmin';

  if (!isAdmin && !isSuperadmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Anda tidak memiliki akses ke halaman ini.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 4, p: 3, boxShadow: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">Data Take Away</Typography>
          <Box>
            <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={fetchTakeaways} sx={{ mr: 2 }}>
              Refresh
            </Button>
            <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={handleNew}>
              Tambah Takeaway
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Tambah Cepat:</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {DEFAULT_TAKEAWAY_OPTIONS.map((opt) => (
              <Button key={opt} variant="outlined" size="small" onClick={() => handleQuickAdd(opt)}>
                + {opt}
              </Button>
            ))}
          </Box>
        </Box>

        {isSuperadmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Takeaway tanpa cabang (kosong) akan tampil di semua cabang sebagai pilihan default.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table sx={{ minWidth: 500 }}>
            <TableHead sx={{ bgcolor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: 'white', width: 60 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Nama Takeaway</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Cabang</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'white' }} align="center">Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : takeaways.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">Belum ada data takeaway</TableCell>
                </TableRow>
              ) : (
                takeaways.map((item, index) => (
                  <TableRow key={item.uuid} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      {item.cabanguuid === null
                        ? <Chip label="Semua Cabang" color="primary" size="small" />
                        : item.Cabang?.namacabang || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          onClick={() => handleEditClick(item)}
                          sx={{ mr: 1 }}
                          disabled={isAdmin && item.cabanguuid === null}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Hapus">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(item.uuid)}
                          disabled={isAdmin && item.cabanguuid === null}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{isEditing ? 'Edit Takeaway' : 'Tambah Takeaway'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ minWidth: 350 }}>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Nama Takeaway"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
              placeholder="Contoh: GRAB, GOJEK, AMBIL SENDIRI"
            />
            {isSuperadmin && (
              <FormControl fullWidth>
                <InputLabel>Cabang (kosongkan untuk semua cabang)</InputLabel>
                <Select
                  name="cabanguuid"
                  value={formData.cabanguuid}
                  onChange={handleInputChange}
                  label="Cabang (kosongkan untuk semua cabang)"
                >
                  <MenuItem value="">Semua Cabang (Global)</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.uuid} value={branch.uuid}>{branch.namacabang}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {isAdmin && (
              <FormControl fullWidth>
                <InputLabel>Cabang</InputLabel>
                <Select
                  name="cabanguuid"
                  value={formData.cabanguuid}
                  onChange={handleInputChange}
                  label="Cabang"
                  required
                  disabled
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.uuid} value={branch.uuid}>{branch.namacabang}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenDialog(false)} color="inherit">Batal</Button>
            <Button type="submit" variant="contained" color="primary">
              {isEditing ? 'Update' : 'Simpan'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default TakeAwayData;